/**
 * "I forgot my password", on the sign-in screen.
 *
 * Nobody in a shop has an e-mail address in the system, so this sends a
 * request to the people who can set a new password: the shop's owner, and for
 * the owner's own password, AppBay. It always says the same thing afterwards,
 * whether or not the name exists - the screen must not become a way to find out
 * who works in a shop.
 */
import { useState } from 'react';
import { useT } from '../../core/i18n/index.js';
import { api, ApiError } from '../../core/api.js';
import { Button, Field } from '../../core/ui/ui.js';

export function ForgotPassword({ code, onBack }: { code: string; onBack: () => void }) {
  const t = useT();
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await api('/auth/password-help', { body: { username: username.trim(), message }, auth: false, account: code || null });
      setSent(true);
    } catch (e) {
      const err = e as ApiError;
      setError(err.code === 'too_many' ? t('login.tooMany') : err.isOffline ? t('error.network') : err.message);
    } finally {
      setBusy(false);
    }
  };

  const wa = `https://wa.me/970597579694?text=${encodeURIComponent(t('forgot.waText', { code: code || '-' }))}`;

  if (sent) {
    return (
      <div className="col">
        <h2>{t('forgot.title')}</h2>
        <p className="note-info" style={{ margin: 0 }}>{t('forgot.sent')}</p>
        <p className="small muted" style={{ margin: 0 }}>
          {t('forgot.ownerHint')}{' '}
          <a href={wa} target="_blank" rel="noopener" style={{ textDecoration: 'underline' }}>
            {t('forgot.whatsapp')}
          </a>
        </p>
        <Button onClick={onBack}>{t('forgot.back')}</Button>
      </div>
    );
  }

  return (
    <form
      className="col"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <h2>{t('forgot.title')}</h2>
      <p className="small muted" style={{ margin: 0 }}>{t('forgot.intro')}</p>
      <Field label={t('login.username')}>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          dir="ltr"
          autoFocus
        />
      </Field>
      <Field label={t('forgot.message')} hint={t('forgot.messageHint')}>
        <input value={message} onChange={(e) => setMessage(e.target.value)} />
      </Field>
      {error ? (
        <p style={{ color: 'var(--danger)', margin: 0 }} role="alert">
          {error}
        </p>
      ) : null}
      <Button type="submit" variant="primary" size="lg" block busy={busy} disabled={!username.trim()}>
        {t('forgot.send')}
      </Button>
      <Button type="button" variant="ghost" onClick={onBack}>
        {t('forgot.back')}
      </Button>
    </form>
  );
}
